
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { Cartridge } from '@/@types/responses/cartridge/Cartridge'
import Toaster from '@meforma/vue-toaster'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { CaliberService } from '@/services/caliber.service'
import { CartridgeMessage } from '@/@types/messages/CartridgeMessage'
import { CartridgeService } from '@/services/cartridge.service'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'CartridgeModal',
  components: {
    CloseSvg,
    LockingButton,
    NumericInput,
    TextInput
  },
  props: {
    caliberId: {
      type: Number,
      required: true
    },
    cartridge: {
      type: Cartridge,
      required: true
    }
  },
  emits: ['cartridge-created', 'cartridge-updated', 'close'],
  setup() {
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const cartridgeService = inject(PROVIDER_CONSTANTS.cartridgeService) as CartridgeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      caliberService,
      cartridgeService,
      toast
    }
  },
  data() {
    return {
      name: this.cartridge.name,
      diameterMinimum: this.cartridge.diameterMinimum,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.name !== this.cartridge.name ||
             this.diameterMinimum !== this.cartridge.diameterMinimum) && (this.name.trim() !== '' && this.diameterMinimum >= 0)
    },
    titleText(): string {
      return this.cartridge.id === 0 ? 'Create Cartridge' : 'Edit Cartridge'
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const cartridgeMessage = new CartridgeMessage(this.diameterMinimum, this.name)
      if (this.cartridge.id === 0)
        this.caliberService.createCartridge(this.caliberId, cartridgeMessage).then(() => {
          this.$emit('cartridge-created')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the cartridge. Please try again.')
          }
        })
      else
        this.cartridgeService.updateCartridge(this.cartridge.id, cartridgeMessage).then(() => {
          this.$emit('cartridge-updated')
          this.$emit('close')
        }).catch(error => {
          this.saving = false
          
          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the cartridge. Please try again.')
          }
        })
    }
  }
})
